import axios from "axios";
import axiosInstance from "../common/axiosInstance";
import { UseMutationOptions, UseMutationResult, UseQueryResult, useMutation, useQuery } from "@tanstack/react-query";
import { sendFileExtensions } from "../../utils/types";

export const PATHS = Object.freeze({
  GET_INTERACTION_COLLATERAL: "interactions/collateral",
  GET_ASSET_DOWNLOAD_URL: "interactions/asset/download-url",
  GET_ASSET_UPLOAD_URL: "interactions/asset/upload-url",
  POST_ASSET_UPLOAD_STATUS: "interactions/asset/upload-status",
  GET_TRANSCRIPT_VIEW: "interactions/view-transcript",
  DELETE_INTERACTION_COLLATERAL: "interactions/delete-asset",
  DELETE_ASSET_BY_GROUP: "interactions/delete-asset-by-group",
  UPLOAD_COLLATERAL: "/interactions/asset/upload",
  DOWNLOAD_COLLATERAL: "/interactions/asset/download",
  POST_VIDEO_CLIPPING: "/interactions/upload/video-clip",
  POST_ASSET_MULTIPART_START: "/interactions/asset/multipart/start",
  POST_ASSET_MULTIPART_COMPLETE: "/interactions/asset/multipart/complete",
  POST_ASSET_MULTIPART_REFRESH_URLS: "/interactions/asset/multipart/refresh-urls",
  POST_ASSET_MULTIPART_ABORT: "/interactions/asset/multipart/abort",
  POST_VIDEO_CLIP_DOWNLOAD: "/interactions/download/video-clip",
  POST_VIDEO_CLIP_GENERATE: "/interactions/generate-video-clip",
});

interface GetCollateralParams {
  interactionId: string;
  impersonatingPorId?: string | undefined;
  accessLevel?: string | undefined;
}
interface GetDownloadUrlParams {
  assetId: string;
  isTeamInteraction: boolean;
  sharedWithMe?: boolean;
  sharedByMe?: boolean;
  interactionId?: string;
  impersonatingPorId?: string | undefined;
  accessLevel?: string | undefined;
}

interface GetUploadUrlParams {
  interactionId: string;
  body: {
    assetName: string;
    assetType: string;
    file: File;
  };
}

interface PostUploadStatusParams {
  interactionId: string;
  assetId: string;
  uploadStatus: "Success" | "Failure";
}

interface GetTranscriptViewParams {
  interactionId: string;
  assetId: string;
}

interface DeleteAssetParams {
  interactionId: string;
  assetId: string;
}

interface DeleteAssetByGroupParams {
  interactionId: string;
  groupId: string;
}

interface DownloadCollateralParams {
  assetId: string;
  isTeamInteraction: boolean;
  sharedWithMe?: boolean;
  sharedByMe?: boolean;
  interactionId: string;
}

interface PostVideoClippingParams {
  interactionId: string;
  assetId: string;
  body: {
    title: string;
    recipientEmail: string;
    videoStartTime: string;   // e.g. "00:02:30"
    videoEndTime: string;     // e.g. "00:02:50"
    emailSubject: string;
    emailDescription: string;
    loggedInUserName: string;
    videoClipName: string;
  };
}

interface interactionCollateralsRequest {
  assetName: string;
  assetType: string;
  assetSource: string;
  createdBy: string;
  aiAnalysisStatus: string;
  uploadStatus: string;
};
interface PostAssetMultipartUploadStartParams {
  interactionId: string;
  file: File;
  interactionCollateralsRequest: interactionCollateralsRequest;
  onUploadProgress?: (progress: number) => void;
  tempUploadId?: string;
  signal?: AbortSignal;
}

interface CompleteMultipartUploadParams {
  uploadId: string;
  objectKey: string;
  parts: {
    partNumber: number;
    eTag: string;
  }[];
}

interface RefreshMultipartUploadUrlsParams {
  uploadId: string;
  objectKey: string;
  partNumbers: number[];
}

interface AbortMultipartUploadParams {
  uploadId: string;
  objectKey: string;
}

interface PostVideoClipGenerateParams {
  interactionId: string;
  originalVideoAssetId: string;
  originalCondensedTranscriptAssetId: string;
  originalRawTranscriptAssetId: string;
  assetName: string;
  groupId: string;
  startTime: number;
  endTime: number;
  videoStartTime: string;
  createdBy: string;
}

const getInteractionCollateral = async (params: GetCollateralParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_INTERACTION_COLLATERAL, {
    params: {
      interactionId: params.interactionId,
      ...(params.impersonatingPorId && { impersonatingPorId: params.impersonatingPorId }),
      ...(params.accessLevel && { accessLevel: params.accessLevel }),
    },
  });
  return response;
};

const useGetInteractionCollateral = (
  params: GetCollateralParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_interaction_collateral", stringifiedParams],
    queryFn: () => getInteractionCollateral(params),
    enabled,
    retry: 0,
  });
};

const getAssetDownloadUrl = async (params: GetDownloadUrlParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ASSET_DOWNLOAD_URL, {
    params: {
      assetId: params.assetId,
      isTeamInteraction: params.isTeamInteraction,
      ...(params.sharedWithMe && { sharedWithMe: params.sharedWithMe }),
      ...(params.sharedByMe && { sharedWithMe: params.sharedByMe }),
      ...(params.interactionId && { interactionId: params.interactionId }),
      ...(params.impersonatingPorId && { impersonatingPorId: params.impersonatingPorId }),
      ...(params.accessLevel && { accessLevel: params.accessLevel }),
    },
  });
  return response;
};

const useGetAssetDownloadUrl = (
  params: GetDownloadUrlParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_asset_download_url", stringifiedParams],
    queryFn: () => getAssetDownloadUrl(params),
    enabled,
    retry: 0,
  });
};

const getAssetUploadUrl = async (params: GetUploadUrlParams): Promise<any> => {
  // Step 1: Get presigned POST URL and fields from backend
  const { data } = await axiosInstance.post(
    `${PATHS.GET_ASSET_UPLOAD_URL}?interactionId=${params.interactionId}`,
    {
      assetName: params.body.assetName,
      assetType: params.body.assetType,
      assetSource: "Account",
    }
  );

  const { uploadUrl } = data;
  const file = params.body.file;

  await axios.put(uploadUrl, file, {
    headers: {
      "Content-Type": file.type,
    },
  });

  return data;
};

const useGetAssetUploadUrl = (
  options?: UseMutationOptions<any, any, GetUploadUrlParams>
): UseMutationResult<any, any, GetUploadUrlParams> => {
  return useMutation({
    mutationFn: getAssetUploadUrl,
    ...options,
  });
};

const postAssetUploadStatus = async (params: PostUploadStatusParams): Promise<any> => {
  const { interactionId, assetId, uploadStatus } = params;
  const url = `${PATHS.POST_ASSET_UPLOAD_STATUS}?interactionId=${interactionId}&assetId=${assetId}&uploadStatus=${uploadStatus}`;
  const response = await axiosInstance.post(url);
  return response;
};

const usePostAssetUploadStatus = (
  options?: UseMutationOptions<any, any, PostUploadStatusParams>
): UseMutationResult<any, any, PostUploadStatusParams> => {
  return useMutation({
    mutationFn: postAssetUploadStatus,
    ...options,
  });
};

const getTranscriptView = async (params: GetTranscriptViewParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_TRANSCRIPT_VIEW,{
    params: {
      interactionId: params.interactionId,
      assetId: params.assetId,
    }
  })
  return response;
}

const UseGetTranscriptView = (params: GetTranscriptViewParams, enabled: boolean) => {
const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_transcript_view", stringifiedParams],
    queryFn: () => getTranscriptView(params),
    enabled: enabled,
    retry: 0,
  });
}

const deleteAsset = async (params: DeleteAssetParams): Promise<any> => {
  const { interactionId, assetId } = params;
  const url = `/interactions/delete-asset?interactionId=${interactionId}&assetId=${assetId}`;
  const response = await axiosInstance.delete(url, {
    headers: {
      accept: "*/*",
    },
  });
  return response;
};

const useDeleteInteractionCollateral = (
  options?: UseMutationOptions<any, any, DeleteAssetParams>
): UseMutationResult<any, any, DeleteAssetParams> => {
  return useMutation({
    mutationFn: deleteAsset,
    ...options,
  });
};

const deleteAssetByGroup = async (params: DeleteAssetByGroupParams): Promise<any> => {
  const { interactionId, groupId } = params;
  const url = `${PATHS.DELETE_ASSET_BY_GROUP}?interactionId=${interactionId}&groupId=${groupId}`;
  const response = await axiosInstance.delete(url, {
    headers: {
      "Content-Type": "application/json",
      accept: "*/*",
    },
  });
  return response;
};

const useDeleteAssetByGroup = (
  options?: UseMutationOptions<any, any, DeleteAssetByGroupParams>
): UseMutationResult<any, any, DeleteAssetByGroupParams> => {
  return useMutation({
    mutationFn: deleteAssetByGroup,
    ...options,
  });
};

const uploadCollateral = async ({
  interactionId,
  file,
  interactionCollateralsRequest,
}: {
  interactionId: string;
  file: File;
  interactionCollateralsRequest: {
    assetSource: string;
    assetType: string;
    assetName: string;
  };
}): Promise<any> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append(
    "interactionCollateralsRequest",
    new Blob([JSON.stringify(interactionCollateralsRequest)], { type: "application/json" })
  );  

  const response = await axiosInstance.post(
    `${PATHS.UPLOAD_COLLATERAL}?interactionId=${interactionId}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );

  return response.data;
};


const useUploadCollateral = (
  options?: UseMutationOptions<any, any, {
    interactionId: string;
    file: File;
    interactionCollateralsRequest: {
      assetSource: string;
      assetType: string;
      assetName: string;
    };
  }>
): UseMutationResult<any, any, {
  interactionId: string;
  file: File;
  interactionCollateralsRequest: {
    assetSource: string;
    assetType: string;
    assetName: string;
  };
}> => {
  return useMutation({
    mutationFn: uploadCollateral,
    ...options,
  });
};

const downloadCollateral = async (params: DownloadCollateralParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.DOWNLOAD_COLLATERAL, {
    params: {
      assetId: params.assetId,
      isTeamInteraction: params.isTeamInteraction,
      ...(params.sharedWithMe && { sharedWithMe: params.sharedWithMe }),
      ...(params.sharedByMe && { sharedByMe: params.sharedByMe }),
      ...(params.interactionId && { interactionId: params.interactionId }),
    },
    responseType: 'blob', // Important for file downloads to handle binary data
  });
  return response;
};

const useDownloadCollateral = (
  params: DownloadCollateralParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["download_collateral", stringifiedParams],
    queryFn: () => downloadCollateral(params),
    enabled,
    retry: 0,
  });
};

const postVideoClipping = async (params: PostVideoClippingParams): Promise<any> => {
  const { interactionId, assetId, body } = params;
  const response = await axiosInstance.post(
    `${PATHS.POST_VIDEO_CLIPPING}?interactionId=${interactionId}&assetId=${assetId}`,
    body,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

const usePostVideoClipping = (
  options?: UseMutationOptions<any, any, PostVideoClippingParams>
): UseMutationResult<any, any, PostVideoClippingParams> => {
  return useMutation({
    mutationFn: postVideoClipping,
    ...options,
  });
};

const postAssetMultipartUploadStart = async (
  params: PostAssetMultipartUploadStartParams
): Promise<any> => {
  const { interactionId, file, interactionCollateralsRequest, onUploadProgress, signal } = params;

  const formData = new FormData();

  formData.append(
    "request",
    new Blob(
      [
        JSON.stringify({
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
          interactionCollateralsRequest,
        }),
      ],
      { type: "application/json" }
    )
  );

  const extension = file.name.split(".").pop()?.toLowerCase();

  if (sendFileExtensions.includes(extension ?? "")) {
    formData.append("file", file);
  }

  const response = await axiosInstance.post(
    `${PATHS.POST_ASSET_MULTIPART_START}?interactionId=${interactionId}`,
    formData,
    {
      signal,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onUploadProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onUploadProgress(percentCompleted);
        }
      },
    }
  );

  return response.data;
};

const usePostAssetMultipartUploadStart = (
  options?: UseMutationOptions<any, any, PostAssetMultipartUploadStartParams>
): UseMutationResult<any, any, PostAssetMultipartUploadStartParams> => {
  return useMutation({
    mutationFn: postAssetMultipartUploadStart,
    ...options,
  });
};

const postAssetMultipartUploadComplete = async (
  params: CompleteMultipartUploadParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.POST_ASSET_MULTIPART_COMPLETE,
    params,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

const usePostAssetMultipartUploadComplete = (
  options?: UseMutationOptions<any, any, CompleteMultipartUploadParams>
): UseMutationResult<any, any, CompleteMultipartUploadParams> => {
  return useMutation({
    mutationFn: postAssetMultipartUploadComplete,
    ...options,
  });
};

const postAssetMultipartRefreshUrls = async (
  params: RefreshMultipartUploadUrlsParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.POST_ASSET_MULTIPART_REFRESH_URLS,
    params,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

const usePostAssetMultipartRefreshUrls = (
  options?: UseMutationOptions<any, any, RefreshMultipartUploadUrlsParams>
): UseMutationResult<any, any, RefreshMultipartUploadUrlsParams> => {
  return useMutation({
    mutationFn: postAssetMultipartRefreshUrls,
    ...options,
  });
};


const postAssetMultipartAbort = async (
  params: AbortMultipartUploadParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.POST_ASSET_MULTIPART_ABORT,
    params,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

const usePostAssetMultipartAbort = (
  options?: UseMutationOptions<any, any, AbortMultipartUploadParams>
): UseMutationResult<any, any, AbortMultipartUploadParams> => {
  return useMutation({
    mutationFn: postAssetMultipartAbort,
    ...options,
  });
};

const postVideoClipDownload = async (params: PostVideoClippingParams): Promise<any> => {
  const { interactionId, assetId, body } = params;
  const response = await axiosInstance.post(
    `${PATHS.POST_VIDEO_CLIP_DOWNLOAD}?interactionId=${interactionId}&assetId=${assetId}`,
    body,
    {
      headers: {
        "Content-Type": "application/json",
      },
      responseType: "blob",
    }
  );

  const blob: Blob = response.data;
  const filename = body.videoClipName || "clip.mp4";
  const contentType = String(response.headers?.["content-type"] || blob?.type || "").toLowerCase();

  if (contentType.includes("json")) {
    const text = await blob.text();
    try {
      const json = text ? JSON.parse(text) : {};
      throw new Error(
        json.error || json.message || "Clip file was not returned"
      );
    } catch (error: any) {
      throw new Error(error?.message || "Clip file was not returned");
    }
  }

  if (!(blob instanceof Blob) || blob.size === 0) {
    throw new Error("Clip file was not returned");
  }

  return { blob, filename };
};

const usePostVideoClipDownload = (
  options?: UseMutationOptions<any, any, PostVideoClippingParams>
): UseMutationResult<any, any, PostVideoClippingParams> => {
  return useMutation({
    mutationFn: postVideoClipDownload,
    ...options,
  });
};

const postVideoClipGenerate = async (params: PostVideoClipGenerateParams): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.POST_VIDEO_CLIP_GENERATE,
    params
  );
  return response.data;
};

const usePostVideoClipGenerate = (
  options?: UseMutationOptions<any, any, PostVideoClipGenerateParams>
): UseMutationResult<any, any, PostVideoClipGenerateParams> => {
  return useMutation({
    mutationFn: postVideoClipGenerate,
    ...options,
  });
};
export interface CollateralService {
  readonly useGetInteractionCollateral: (
    params: GetCollateralParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;
  readonly useGetAssetDownloadUrl: (
    params: GetDownloadUrlParams,
    enabled: boolean
  ) => UseQueryResult<any, any>; 
  readonly useGetAssetUploadUrl: (
    options?: UseMutationOptions<any, any, GetUploadUrlParams>
  ) => UseMutationResult<any, any, GetUploadUrlParams>;
  readonly usePostAssetUploadStatus: (
    options?: UseMutationOptions<any, any, PostUploadStatusParams>
  ) => UseMutationResult<any, any, PostUploadStatusParams>; 
  readonly UseGetTranscriptView: (
    params: GetTranscriptViewParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;
  readonly useDeleteInteractionCollateral: (
    options?: UseMutationOptions<any, any, DeleteAssetParams>
  ) => UseMutationResult<any, any, DeleteAssetParams>;  
  readonly useDeleteAssetByGroup: (
    options?: UseMutationOptions<any, any, DeleteAssetByGroupParams>
  ) => UseMutationResult<any, any, DeleteAssetByGroupParams>;  
  readonly useUploadCollateral: (
    options?: UseMutationOptions<any, any, {
      interactionId: string;
      file: File;
      interactionCollateralsRequest: {
        assetSource: string;
        assetType: string;
        assetName: string;
      };
    }>
  ) => UseMutationResult<any, any, {
    interactionId: string;
    file: File;
    interactionCollateralsRequest: {
      assetSource: string;
      assetType: string;
      assetName: string;
    };
  }>;

  readonly useDownloadCollateral: (
    params: DownloadCollateralParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly usePostVideoClipping: (
    options?: UseMutationOptions<any, any, PostVideoClippingParams>
  ) => UseMutationResult<any, any, PostVideoClippingParams>;  

  readonly usePostAssetMultipartUploadStart: (
    options?: UseMutationOptions<any, any, PostAssetMultipartUploadStartParams>
  ) => UseMutationResult<any, any, PostAssetMultipartUploadStartParams>;

  readonly usePostAssetMultipartUploadComplete: (
    options?: UseMutationOptions<any, any, CompleteMultipartUploadParams>
  ) => UseMutationResult<any, any, CompleteMultipartUploadParams>;

  readonly usePostAssetMultipartRefreshUrls: (
    options?: UseMutationOptions<any, any, RefreshMultipartUploadUrlsParams>
  ) => UseMutationResult<any, any, RefreshMultipartUploadUrlsParams>;

  readonly usePostAssetMultipartAbort: (
    options?: UseMutationOptions<any, any, AbortMultipartUploadParams>
  ) => UseMutationResult<any, any, AbortMultipartUploadParams>;

  readonly usePostVideoClipDownload: (
    options?: UseMutationOptions<any, any, PostVideoClippingParams>
  ) => UseMutationResult<any, any, PostVideoClippingParams>; 

  readonly usePostVideoClipGenerate: (
    options?: UseMutationOptions<any, any, PostVideoClipGenerateParams>
  ) => UseMutationResult<any, any, PostVideoClipGenerateParams>;

}

const CollateralService: CollateralService = Object.freeze({
  useGetInteractionCollateral,
  useGetAssetDownloadUrl,
  useGetAssetUploadUrl,
  usePostAssetUploadStatus,
  UseGetTranscriptView,
  useDeleteInteractionCollateral,
  useDeleteAssetByGroup,
  useUploadCollateral,
  useDownloadCollateral,
  usePostVideoClipping,
  usePostVideoClipDownload,
  usePostAssetMultipartUploadStart,
  usePostAssetMultipartUploadComplete,
  usePostAssetMultipartRefreshUrls,
  usePostAssetMultipartAbort,
  usePostVideoClipGenerate,
});

export default CollateralService;
